export interface ReadingSectionConfig {
  key: string;
  label: string;
  aliases: string[];
  questionCount: number;
  description: string;
}

export const READING_STRUCTURE: ReadingSectionConfig[] = [
  {
    key: "documents_vie_quotidienne",
    label: "Documents de la vie quotidienne",
    aliases: [
      "Documents de la vie quotidienne",
      "Documents divers",
      "Vie quotidienne",
      "Section A"
    ],
    questionCount: 7,
    description: "Anúncios, publicidades, menus e notas curtas."
  },
  {
    key: "phrases_textes_lacunaires",
    label: "Phrases et textes lacunaires",
    aliases: [
      "Phrases et textes lacunaires",
      "Phrases lacunaires",
      "Textes lacunaires",
      "Section B"
    ],
    questionCount: 10,
    description: "Exercícios de completar lacunas, gramática e vocabulário em contexto."
  },
  {
    key: "lecture_rapide",
    label: "Lecture rapide de textes et de graphiques",
    aliases: [
      "Lecture rapide de textes et de graphiques",
      "Lecture rapide de textes et graphiques",
      "Lecture rapide"
    ],
    questionCount: 5,
    description: "Extração rápida de informações de tabelas, gráficos ou textos breves."
  },
  {
    key: "documents_admin_professionnels",
    label: "Documents administratifs et professionnels",
    aliases: [
      "Documents administratifs et professionnels",
      "Documents administratifs",
      "Documents professionnels"
    ],
    questionCount: 10,
    description: "Cartas formais, e-mails de trabalho e documentos administrativos."
  },
  {
    key: "articles_presse",
    label: "Articles de presse",
    aliases: [
      "Articles de presse",
      "Article de presse",
      "Section C"
    ],
    questionCount: 8,
    description: "Artigos de jornal mais longos e complexos, opinião e análise."
  }
];

/**
 * Normalizes any variation of a section name into the official reading section key.
 */
export function normalizeReadingSection(sectionName: string): string {
  if (!sectionName) return "documents_vie_quotidienne";
  const lower = sectionName.trim().toLowerCase();

  for (const item of READING_STRUCTURE) {
    if (item.aliases.some(alias => alias.toLowerCase() === lower) || lower === item.key) {
      return item.key;
    }
  }

  if (lower.includes("quotidienne") || lower.includes("divers") || lower.includes("quotidien")) return "documents_vie_quotidienne";
  if (lower.includes("lacunaires") || lower.includes("lacunaire") || lower.includes("phrases") || lower.includes("textes")) return "phrases_textes_lacunaires";
  if (lower.includes("lecture") || lower.includes("rapide") || lower.includes("graphique") || lower.includes("graphiques")) return "lecture_rapide";
  if (lower.includes("administratif") || lower.includes("professionnel") || lower.includes("admin")) return "documents_admin_professionnels";
  if (lower.includes("article") || lower.includes("presse") || lower.includes("jornal")) return "articles_presse";

  if (lower.includes("section a")) return "documents_vie_quotidienne";
  if (lower.includes("section b")) return "phrases_textes_lacunaires";
  if (lower.includes("section c")) return "articles_presse";

  return "documents_vie_quotidienne";
}

/**
 * Returns the official label for a given reading key.
 */
export function getReadingLabelByKey(key: string): string {
  const match = READING_STRUCTURE.find(item => item.key === key);
  return match ? match.label : "Documents de la vie quotidienne";
}

// ─────────────────────────────────────────────────────────────────────────────
// LISTENING STRUCTURE — 40 official questions across 4 sections
// ─────────────────────────────────────────────────────────────────────────────

export interface ListeningSectionConfig {
  key: string;
  label: string;
  focus: string;
  questionCount: number;
  aliases: string[];
}

export const LISTENING_STRUCTURE: ListeningSectionConfig[] = [
  {
    key: "Dialogues associés à des images",
    label: "Dialogues associés à des images",
    focus: "Documents courts et images",
    questionCount: 8,
    aliases: [
      "Dialogues associés à des images",
      "Dialogues associés a des images",
      "Dialogues / images",
      "Section A",
      "dialogues_images",
      "dialogues_associes_images"
    ]
  },
  {
    key: "Messages vocaux",
    label: "Messages vocaux",
    focus: "Messages vocaux, radio et enquêtes",
    questionCount: 24,
    aliases: [
      "Messages vocaux",
      "Section B",
      "messages_vocaux"
    ]
  },
  {
    key: "Annonces publiques",
    label: "Annonces publiques",
    focus: "Annonces publiques, opinions",
    questionCount: 4,
    aliases: [
      "Annonces publiques",
      "Section C",
      "annonces_publiques"
    ]
  },
  {
    key: "Dialogues / Reportages courts",
    label: "Dialogues / Reportages courts",
    focus: "Reconnaissance de sons/phrases",
    questionCount: 2,
    aliases: [
      "Dialogues / Reportages courts",
      "Dialogues et reportages courts",
      "Section D",
      "dialogues_reportages_courts"
    ]
  },
  {
    key: "Discussions / Conférences longues",
    label: "Discussions / Conférences longues",
    focus: "Discussions / Conférences longues",
    questionCount: 2,
    aliases: [
      "Discussions / Conférences longues",
      "Discussions et conférences longues",
      "Section E",
      "discussions_conferences_longues"
    ]
  }
];

/**
 * Normalizes any variation of a Listening section name into the official key.
 * Returns: "section_a" | "section_b" | "section_c" | "section_d"
 */
export function normalizeListeningSection(sectionName: string): string {
  const s = String(sectionName || "").trim().toLowerCase();

  if (
    s.includes("dialogues associés") ||
    s.includes("dialogues associes") ||
    s.includes("images") ||
    s === "section a" ||
    s === "dialogues_images"
  ) {
    return "Dialogues associés à des images";
  }

  if (
    s.includes("messages vocaux") ||
    s === "section b" ||
    s === "messages_vocaux"
  ) {
    return "Messages vocaux";
  }

  if (
    s.includes("annonces publiques") ||
    s === "section c" ||
    s === "annonces_publiques"
  ) {
    return "Annonces publiques";
  }

  if (
    s.includes("reportages courts") ||
    s === "section d" ||
    s === "dialogues_reportages_courts"
  ) {
    return "Dialogues / Reportages courts";
  }

  if (
    s.includes("conférences longues") ||
    s.includes("conferences longues") ||
    s === "section e" ||
    s === "discussions_conferences_longues"
  ) {
    return "Discussions / Conférences longues";
  }

  return sectionName || "Messages vocaux";
}

/**
 * Returns the official label for a given listening key.
 */
export function getListeningLabelByKey(key: string): string {
  const match = LISTENING_STRUCTURE.find(item => item.key === key);
  return match ? match.label : key;
}

/**
 * Returns the focus description for a given listening key.
 */
export function getListeningFocusByKey(key: string): string {
  const match = LISTENING_STRUCTURE.find(item => item.key === key);
  return match ? match.focus : "";
}

// ─────────────────────────────────────────────────────────────────────────────
// WRITING STRUCTURE — 2 official tasks (Section A + B = 60 min total)
// ─────────────────────────────────────────────────────────────────────────────

export interface WritingSectionConfig {
  key: string;
  label: string;
  taskLabel: string;
  aliases: string[];
  questionCount: number;
  minimumWords: number;
  suggestedTimeMinutes: number;
  description: string;
}

export const WRITING_STRUCTURE: WritingSectionConfig[] = [
  {
    key: "section_a",
    label: "Section A",
    taskLabel: "Rédaction d'un article / fait divers",
    aliases: [
      "Section A",
      "Tâche 1",
      "Tache 1",
      "Task 1",
      "Fait divers",
      "Article / notícia",
      "Rédaction de fait divers",
      "Tâche A",
      "Fait Divers"
    ],
    questionCount: 1,
    minimumWords: 80,
    suggestedTimeMinutes: 25,
    description: "Vous recevez le début d'un article de journal, généralement un fait divers, et vous devez écrire la continuation et le dénouement."
  },
  {
    key: "section_b",
    label: "Section B",
    taskLabel: "Argumentation",
    aliases: [
      "Section B",
      "Tâche 2",
      "Tache 2",
      "Task 2",
      "Argumentation",
      "Opinion",
      "Point de vue",
      "Tâche B",
      "Lettre de Opinião",
      "Letra de Opinião",
      "Lettre d'opinion",
      "Letra de opinião"
    ],
    questionCount: 1,
    minimumWords: 200,
    suggestedTimeMinutes: 35,
    description: "Vous recevez un thème ou une affirmation de journal et vous devez exprimer votre point de vue avec des arguments solides pour convaincre le lecteur."
  }
];

/**
 * Normalizes any variation of a Writing section name into the official key.
 * Returns: "section_a" | "section_b"
 */
export function normalizeWritingSection(sectionName: string): string {
  if (!sectionName) return "section_a";

  const lower = sectionName.trim().toLowerCase();

  for (const item of WRITING_STRUCTURE) {
    if (
      item.key === lower ||
      item.aliases.some(alias => alias.toLowerCase() === lower)
    ) {
      return item.key;
    }
  }

  // Heuristic partial matches
  if (
    lower.includes("tâche 1") || lower.includes("tache 1") || lower.includes("task 1") ||
    lower.includes("fait divers") || lower.includes("article") || lower.includes("notícia") ||
    lower.includes("section a") || lower === "a"
  ) {
    return "section_a";
  }
  if (
    lower.includes("tâche 2") || lower.includes("tache 2") || lower.includes("task 2") ||
    lower.includes("argumentation") || lower.includes("opinion") || lower.includes("opinião") ||
    lower.includes("point de vue") || lower.includes("letra") || lower.includes("lettre") ||
    lower.includes("section b") || lower === "b"
  ) {
    return "section_b";
  }

  return "section_a"; // default to first task
}

/**
 * Returns the official label for a given writing key.
 */
export function getWritingLabelByKey(key: string): string {
  const match = WRITING_STRUCTURE.find(item => item.key === key);
  return match ? match.label : "Section A";
}

/**
 * Returns the full config for a given writing key.
 */
export function getWritingConfigByKey(key: string): WritingSectionConfig | undefined {
  return WRITING_STRUCTURE.find(item => item.key === key);
}

// ─────────────────────────────────────────────────────────────────────────────
// LISTENING DIFFICULTY MAP — automatic difficulty by audio type (sous-partie)
// ─────────────────────────────────────────────────────────────────────────────

export interface ListeningDifficultyEntry {
  aliases: string[];
  difficultyLabel: string;
  probableLevel: string;
  difficultyValue: number;
}

export const LISTENING_DIFFICULTY_MAP: ListeningDifficultyEntry[] = [
  {
    aliases: [
      "Dialogues associés à des images",
      "Association de photos",
      "Section A",
      "Images",
      "Dialogues courts et images",
      "Documents courts et images"
    ],
    difficultyLabel: "Muito fácil a fácil",
    probableLevel: "A1-A2",
    difficultyValue: 1
  },
  {
    aliases: [
      "Annonces publiques",
      "Anúncios públicos"
    ],
    difficultyLabel: "Fácil a médio",
    probableLevel: "A2-B1",
    difficultyValue: 2
  },
  {
    aliases: [
      "Messages courts",
      "Situations téléphoniques",
      "Messages courts / situations téléphoniques",
      "Messages courts/situations téléphoniques"
    ],
    difficultyLabel: "Médio",
    probableLevel: "B1",
    difficultyValue: 3
  },
  {
    aliases: [
      "Micro-trottoirs",
      "Micro trottoirs"
    ],
    difficultyLabel: "Médio a difícil",
    probableLevel: "B1-B2",
    difficultyValue: 4
  },
  {
    aliases: [
      "Chroniques radio",
      "Chronique radio"
    ],
    difficultyLabel: "Difícil",
    probableLevel: "B2",
    difficultyValue: 4
  },
  {
    aliases: [
      "Interviews",
      "Interview"
    ],
    difficultyLabel: "Difícil",
    probableLevel: "B2-C1",
    difficultyValue: 5
  },
  {
    aliases: [
      "Reportages",
      "Reportage"
    ],
    difficultyLabel: "Muito difícil",
    probableLevel: "C1",
    difficultyValue: 5
  },
  {
    aliases: [
      "Documents divers",
      "Document divers"
    ],
    difficultyLabel: "Difícil a muito difícil",
    probableLevel: "B2-C1/C2",
    difficultyValue: 5
  }
];

/**
 * List of audio type labels shown to the user in the UI select.
 */
export const LISTENING_AUDIO_TYPE_OPTIONS: string[] = [
  "Dialogues associés à des images",
  "Annonces publiques",
  "Messages courts / situations téléphoniques",
  "Micro-trottoirs",
  "Chroniques radio",
  "Interviews",
  "Reportages",
  "Documents divers"
];

export interface AutomaticDifficultyResult {
  /** Numeric difficulty value (1–5) */
  difficulty: number;
  /** Friendly label (e.g. "Médio a difícil") */
  difficultyLabel: string;
  /** CEFR level range (e.g. "B1-B2") */
  probableLevel: string;
  /** Legacy string used for backward-compat filter ("Fácil" | "Médio" | "Difícil") */
  difficultyLegacy: "Fácil" | "Médio" | "Difícil";
}

/**
 * Returns the automatic difficulty result for a given competency/section/audioType.
 *
 * Priority:
 * - Listening: audioType first → section fallback (Section A → "Dialogues associés à des images")
 * - Writing: Section A → B1-B2, Section B → B2-C1
 * - Reading: generic fallback
 */
export function getAutomaticDifficulty(params: {
  competency: string;
  section?: string;
  audioType?: string;
}): AutomaticDifficultyResult {
  const { competency, section = "", audioType = "" } = params;
  const comp = competency.toLowerCase();

  // ── LISTENING ────────────────────────────────────────────────────────────
  if (comp === "listening") {
    const sec = (section || audioType || "").trim().toLowerCase();

    let entry: ListeningDifficultyEntry | undefined = LISTENING_DIFFICULTY_MAP.find(e =>
      e.aliases.some(a => a.toLowerCase() === sec)
    );

    // Partial match fallback
    if (!entry) {
      entry = LISTENING_DIFFICULTY_MAP.find(e =>
        e.aliases.some(a => sec.includes(a.toLowerCase()) || a.toLowerCase().includes(sec))
      );
    }

    // Default fallback to "Dialogues associés à des images" if section name is generic "Section A"
    if (!entry && (sec.includes("section a") || sec === "section_a" || sec === "a")) {
      entry = LISTENING_DIFFICULTY_MAP[0]; // Dialogues associés à des images
    }

    if (entry) {
      return {
        difficulty: entry.difficultyValue,
        difficultyLabel: entry.difficultyLabel,
        probableLevel: entry.probableLevel,
        difficultyLegacy: entry.difficultyValue <= 2 ? "Fácil" : entry.difficultyValue <= 3 ? "Médio" : "Difícil"
      };
    }

    // Generic listening fallback
    return {
      difficulty: 3,
      difficultyLabel: "Médio",
      probableLevel: "B1",
      difficultyLegacy: "Médio"
    };
  }

  // ── WRITING ──────────────────────────────────────────────────────────────
  if (comp === "writing") {
    const sec = section.trim().toLowerCase();
    if (sec.includes("section b") || sec === "section_b" || sec === "b" ||
        sec.includes("tâche 2") || sec.includes("tache 2") || sec.includes("argumentation") ||
        sec.includes("opinion") || sec.includes("letra de opinião")) {
      return {
        difficulty: 4,
        difficultyLabel: "Automática",
        probableLevel: "B2-C1",
        difficultyLegacy: "Difícil"
      };
    }
    // Section A default
    return {
      difficulty: 3,
      difficultyLabel: "Automática",
      probableLevel: "B1-B2",
      difficultyLegacy: "Médio"
    };
  }

  // ── READING (generic fallback) ────────────────────────────────────────────
  return {
    difficulty: 3,
    difficultyLabel: "Automática",
    probableLevel: "Variável",
    difficultyLegacy: "Médio"
  };
}
